import type { ProjectView, StoredReader } from "@esi/shared";
import { t } from "../i18n/i18n.ts";
import { describeReaders } from "../projects/readers.ts";

/** "Project audience", or "Restricted · 6 people", for a file's stored readers. */
export function fileAudience(
  readers: readonly StoredReader[],
  project: Pick<ProjectView, "id" | "owner">,
  userId: string,
): { text: string; restricted: boolean } {
  const restricted = !readers.some((r) => r.kind === "project");
  const described = describeReaders(readers, project, userId);
  return {
    restricted,
    text: restricted ? `${t("audience.restricted")} · ${described}` : described,
  };
}

/** "Restricted · 4 people" for a resolved reader list (save preview, publish preview). */
export function peopleAudience(count: number, onlyYou: boolean): string {
  return `${t("audience.restricted")} · ${onlyYou ? t("readers.onlyYou") : t("readers.people", { count })}`;
}

/**
 * Who can read a conversation, as its viewer sees it: the owner sees "Only you", "You and 3
 * people" or, once shared with the project, "You, the project and 3 people"; a recipient sees
 * who shared it with them, and whether through the project.
 */
export function conversationAudience(
  input:
    | { owned: true; shares: number; project?: boolean }
    | { owned: false; ownerName: string; project?: boolean },
): string {
  if (!input.owned) {
    return input.project
      ? t("audience.projectSharedBy", { name: input.ownerName })
      : t("audience.sharedBy", { name: input.ownerName });
  }
  if (input.project) {
    return input.shares === 0
      ? t("audience.youAndProject")
      : t("audience.youProjectAndShared", { count: input.shares });
  }
  return input.shares === 0
    ? t("audience.onlyYou")
    : t("audience.youAndShared", { count: input.shares });
}
